/**
 * ADR-0230 §2 — AI classification of agent-registry candidates.
 *
 * Closed vocabulary shared by the origin classifier (which validates the
 * model's structured output against it), the Automatic approval gates and
 * the ops2026 board (which explains each kind in plain words).
 *
 * The model reads attacker-controllable text (the User-Agent). Nothing in
 * this module trusts it: every field is checked against a closed enum,
 * a grammar or the UA itself by `parseAiRegistryClassification`
 * (services/api/src/agents/registry-ai-classifier.ts), and the Automatic
 * gates add deterministic evidence on top (ADR-0230 §3).
 */
export const AI_REGISTRY_KINDS = [
    'ai_crawler',
    'ai_assistant',
    'ai_search',
    'search_engine',
    'seo_tool',
    'monitoring',
    'feed_reader',
    'link_preview',
    'security_scanner',
    'hostile',
    'human_browser',
    'unknown',
];
export function isAiRegistryKind(v) {
    return typeof v === 'string' && AI_REGISTRY_KINDS.includes(v);
}
/**
 * Registry category for each kind. `null` = never registered
 * (`human_browser` is people; `unknown` needs a person to decide).
 */
export const AI_KIND_CATEGORY = {
    ai_crawler: 'ai_crawler',
    ai_assistant: 'ai_assistant',
    // AI search indexers (Exa, Linkup, Parallel …) are bundled as ai_crawler.
    ai_search: 'ai_crawler',
    search_engine: 'search',
    seo_tool: 'seo',
    monitoring: 'monitoring',
    feed_reader: 'monitoring',
    link_preview: 'social_preview',
    security_scanner: 'hostile',
    hostile: 'hostile',
    human_browser: null,
    unknown: null,
};
/** Kinds Automatic never approves (ADR-0230 §3). */
export const AUTO_DENIED_AI_KINDS = new Set([
    'human_browser',
    'hostile',
    'unknown',
    'security_scanner',
]);
/** Minimum model confidence for Automatic. */
export const AUTO_MIN_AI_CONFIDENCE = 0.85;
export const AI_KIND_GROUP = {
    ai_assistant: 'agent',
    ai_crawler: 'bot',
    ai_search: 'bot',
    search_engine: 'bot',
    seo_tool: 'bot',
    monitoring: 'bot',
    feed_reader: 'bot',
    link_preview: 'bot',
    security_scanner: 'threat',
    hostile: 'threat',
    human_browser: 'person',
    unknown: 'unknown',
};
/** Short label per kind. */
export const AI_KIND_LABEL = {
    ai_crawler: 'AI crawler',
    ai_assistant: 'AI assistant (agent)',
    ai_search: 'AI search',
    search_engine: 'Search engine',
    seo_tool: 'SEO tool',
    monitoring: 'Monitoring',
    feed_reader: 'Feed reader',
    link_preview: 'Link preview',
    security_scanner: 'Security scanner',
    hostile: 'Hostile',
    human_browser: 'Person (browser)',
    unknown: 'Unknown',
};
/** One plain sentence per kind, shown on the ops2026 board. */
export const AI_KIND_EXPLANATION = {
    ai_assistant: 'An agent: an AI fetching a page right now because a person asked it to.',
    ai_crawler: 'A bot that collects pages to train an AI model or build its index.',
    ai_search: 'A bot that indexes pages so an AI search product can answer questions.',
    search_engine: 'A bot from a classic search engine, building its search index.',
    seo_tool: 'A bot from an SEO or marketing tool, measuring links and rankings.',
    monitoring: 'A bot checking that a site is up, or watching it for changes or brand mentions.',
    feed_reader: 'A bot fetching a site\'s RSS or Atom feed for its subscribers.',
    link_preview: 'A bot that fetches a page to draw the preview card when someone shares a link.',
    security_scanner: 'A scanner probing for vulnerabilities. Never auto-approved; a person decides.',
    hostile: 'Abusive or exploit traffic. Never auto-approved; a person decides.',
    human_browser: 'A person\'s web browser, not a bot. Never registered.',
    unknown: 'The AI could not tell what this is. A person decides.',
};
//# sourceMappingURL=agent-registry-ai.js.map